import { useState, type ReactNode } from "react";
import { Modal } from "../molecules/Modal.js";
import { useDownload } from "../../api/useDownload.js";
import { Button, Icon, useT } from "@mmm/ui";
type ExportKind = "word" | "pdf";

interface ExportTriggerProps {
  onClick: () => void;
  disabled: boolean;
  busy: boolean;
}

interface ExportPreviewProps {
  preview: ReactNode;
  options?: ReactNode;
  trigger?: (props: ExportTriggerProps) => ReactNode;
  onWord: () => void | Promise<void>;
  onPdf: () => void | Promise<void>;
}

export function ExportPreview({ preview, options, trigger, onWord, onPdf }: ExportPreviewProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const { pending, busy, start } = useDownload<ExportKind>();
  const preparing = t("common.preparingDownload");
  const openPreview = () => setOpen(true);

  const renderFormatButton = (kind: ExportKind) => {
    const isPending = pending === kind;
    const label = kind === "word" ? t("common.word") : t("common.pdf");
    return (
      <Button
        key={kind}
        variant="secondary"
        disabled={busy}
        aria-busy={isPending}
        onClick={() => start(kind, kind === "word" ? onWord : onPdf)}
      >
        {isPending ? (
          <>
            <span className="mmm-btn-busy-dot" aria-hidden="true" />
            {preparing}
          </>
        ) : (
          label
        )}
      </Button>
    );
  };

  return (
    <>
      {trigger ? (
        trigger({ onClick: openPreview, disabled: busy, busy })
      ) : (
        <div className="mmm-export-strip mmm-export-strip--preview-trigger">
          <Button variant="secondary" disabled={busy} onClick={openPreview}>
            <Icon name="export" />
            {t("common.export")}
          </Button>
        </div>
      )}

      {open && (
        <Modal
          onClose={() => setOpen(false)}
          width="50vw"
          className="mmm-modal--export-preview"
          ariaLabelledBy="mmm-export-preview-title"
        >
          <h2 id="mmm-export-preview-title">{t("common.exportPreview")}</h2>
          {options && <div className="mmm-export-preview-options">{options}</div>}
          <div className="mmm-export-preview-scroll">{preview}</div>
          <div className="mmm-modal-actions mmm-export-preview-actions">
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>
              {t("common.cancel")}
            </Button>
            {renderFormatButton("word")}
            {renderFormatButton("pdf")}
          </div>
        </Modal>
      )}
    </>
  );
}
